import Decimal from "decimal.js";
export const ZERO = new Decimal(0);
/**
 * Configure Decimal.js for German locale and consistent precision
 */
Decimal.set({
    precision: 30,
    rounding: Decimal.ROUND_HALF_UP,
    toExpNeg: -7,
    toExpPos: 30,
});
/**
 * Converts a value to a Decimal instance safely
 * @param value - The value to convert (string, number, or Decimal)
 * @returns A Decimal instance
 */
export function toDecimal(value) {
    if (value instanceof Decimal) {
        return value;
    }
    if (typeof value === "string") {
        // Handle German number format (comma as decimal separator)
        // First check if it's already in standard format (contains dot as decimal separator)
        if (value.includes(".") && !value.includes(",")) {
            return new Decimal(value);
        }
        // Handle German format (comma as decimal separator, dots as thousands separators)
        const normalizedValue = value.replace(/\./g, "").replace(",", ".");
        if (normalizedValue !== "") {
            return new Decimal(normalizedValue);
        }
        return new Decimal("0");
    }
    return new Decimal(value);
}
/**
 * Converts a value to a number safely, handling precision issues
 * @param value - The value to convert
 * @returns A number with proper precision
 */
export function toNumber(value) {
    return toDecimal(value).toNumber();
}
/**
 * Formats a number for display in German locale
 * @param value - The value to format
 * @param precision - Number of decimal places (default: 6)
 * @returns Formatted string
 */
export function formatNumber(value, precision = 6) {
    const decimal = toDecimal(value);
    // Handle special cases
    if (decimal.isNaN())
        return "NaN";
    if (!decimal.isFinite())
        return decimal.isPositive() ? "∞" : "-∞";
    // Format with German locale
    return decimal.toNumber().toLocaleString("de-DE", {
        minimumFractionDigits: 0,
        maximumFractionDigits: precision,
    });
}
/**
 * Parses a string value to a number, handling German number format
 * @param value - The string value to parse
 * @returns The parsed number or 0 if invalid
 */
export function parseNumber(value) {
    if (!value || value.trim() === "")
        return 0;
    try {
        // Trim whitespace
        const trimmedValue = value.trim();
        // Handle German number format (comma as decimal separator)
        // First check if it's already in standard format (contains dot as decimal separator)
        if (trimmedValue.includes(".") && !trimmedValue.includes(",")) {
            const decimal = new Decimal(trimmedValue);
            if (decimal.isNaN())
                return 0;
            return decimal.toNumber();
        }
        // Handle German format (comma as decimal separator, dots as thousands separators)
        const normalizedValue = trimmedValue.replace(/\./g, "").replace(",", ".");
        const decimal = new Decimal(normalizedValue);
        if (decimal.isNaN())
            return 0;
        return decimal.toNumber();
    }
    catch {
        return 0;
    }
}
/**
 * Adjusts decimal precision of a value
 * @param value - The value to adjust
 * @param precision - The number of decimal places
 * @returns Formatted string with the specified precision
 */
export function adjustDecimalPrecision(value, precision) {
    const decimal = toDecimal(value);
    if (decimal.isNaN())
        return "0";
    if (!decimal.isFinite())
        return decimal.isPositive() ? "∞" : "-∞";
    return decimal.toNumber().toLocaleString("de-DE", {
        minimumFractionDigits: precision,
        maximumFractionDigits: precision,
    });
}
/**
 * Performs arithmetic operations with proper decimal precision
 */
export const DecimalMath = {
    add: (a, b) => {
        return toDecimal(a).plus(toDecimal(b)).toNumber();
    },
    subtract: (a, b) => {
        return toDecimal(a).minus(toDecimal(b)).toNumber();
    },
    multiply: (a, b) => {
        return toDecimal(a).times(toDecimal(b)).toNumber();
    },
    divide: (a, b) => {
        return toDecimal(a).dividedBy(toDecimal(b)).toNumber();
    },
    round: (value, precision = 0) => {
        return toDecimal(value).toDecimalPlaces(precision).toNumber();
    },
    floor: (value) => {
        return toDecimal(value).floor().toNumber();
    },
    ceil: (value) => {
        return toDecimal(value).ceil().toNumber();
    },
};
/**
 * Validates if a string represents a valid number in German format
 * @param value - The string to validate
 * @returns True if valid, false otherwise
 */
export function isValidNumber(value) {
    if (!value || value.trim() === "")
        return false;
    try {
        const normalizedValue = value.replace(/\./g, "").replace(",", ".");
        const decimal = new Decimal(normalizedValue);
        return !decimal.isNaN();
    }
    catch {
        return false;
    }
}
/**
 * Normalizes a number string from German format to standard format
 * @param value - The German formatted number string
 * @returns Normalized number string
 */
export function normalizeNumberString(value) {
    // First check if it's already in standard format (contains dot as decimal separator)
    if (value.includes(".") && !value.includes(",")) {
        return value;
    }
    // Handle German format (comma as decimal separator, dots as thousands separators)
    return value.replace(/\./g, "").replace(",", ".");
}
/**
 * Converts a standard number string to German format
 * @param value - The standard number string
 * @returns German formatted number string
 */
export function toGermanFormat(value) {
    const decimal = toDecimal(value);
    return decimal.toNumber().toLocaleString("de-DE", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    });
}
